import React, { useEffect, useState, useCallback } from 'react';

// Import all gallery images explicitly
import img1 from '../images/gallery/Messenger_creation_C6DB82C9-7F88-4FC7-85DA-F1B330E42A5C.webp';
import img2 from '../images/gallery/Messenger_creation_8B454269-8596-4FDB-95B2-C8285B5FA101.webp';
import img3 from '../images/gallery/20231206_082353.webp';
import img4 from '../images/gallery/20231206_121419.webp';
import img5 from '../images/gallery/20240114_102414.webp';
import img6 from '../images/gallery/20240114_114440.webp';
import img7 from '../images/gallery/20231206_083615.webp';
import img8 from '../images/gallery/20231206_120912.webp';
import img9 from '../images/gallery/20240511_091437.webp';
import img10 from '../images/gallery/20240511_115504.webp';
import img11 from '../images/gallery/20240628_134418.webp';
import img12 from '../images/gallery/20240628_154234.webp';
import img13 from '../images/gallery/20250104_083223.webp';
import img14 from '../images/gallery/20250104_102722.webp';
import img15 from '../images/gallery/20250114_084445.webp';
import img16 from '../images/gallery/20250114_111715.webp';
import img17 from '../images/gallery/20250206_084647.webp';
import img18 from '../images/gallery/20250206_101537.webp';

// Structure images as before/after pairs
const imagePairs = [
  {
    id: 1,
    before: img1,
    after: img2,
    title: "Lawn Transformation #1",
    description: "Complete lawn restoration and maintenance"
  },
  {
    id: 2,
    before: img3,
    after: img4,
    title: "Lawn Transformation #2",
    description: "Professional mowing and edging service"
  },
  {
    id: 3,
    before: img5,
    after: img6,
    title: "Lawn Transformation #3",
    description: "Weed control and lawn care"
  },
  {
    id: 4,
    before: img7,
    after: img8,
    title: "Lawn Transformation #4",
    description: "Comprehensive garden maintenance"
  },
  {
    id: 5,
    before: img9,
    after: img10,
    title: "Lawn Transformation #5",
    description: "Seasonal lawn preparation"
  },
  {
    id: 6,
    before: img11,
    after: img12,
    title: "Lawn Transformation #6",
    description: "Summer lawn maintenance"
  },
  {
    id: 7,
    before: img13,
    after: img14,
    title: "Lawn Transformation #7",
    description: "New year lawn care"
  },
  {
    id: 8,
    before: img15,
    after: img16,
    title: "Lawn Transformation #8",
    description: "Professional garden maintenance"
  },
  {
    id: 9,
    before: img17,
    after: img18,
    title: "Lawn Transformation #9",
    description: "Latest lawn transformation"
  }
];

// Flatten all images for lightbox navigation
const allImages = imagePairs.flatMap(pair => [pair.before, pair.after]);

const Gallery = ({ withHeading = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [galleryViewed, setGalleryViewed] = useState(false);

  // GA4 tracking function
  const trackGalleryEvent = (eventName, parameters = {}) => {
    try {
      if (window.gtag) {
        window.gtag('event', eventName, {
          event_category: 'gallery_interaction',
          event_label: 'before_after_gallery',
          ...parameters
        });
      }
    } catch (error) {
      console.warn('GA4 tracking error:', error);
    }
  };

  // Track gallery view when component mounts
  useEffect(() => {
    if (!galleryViewed) {
      trackGalleryEvent('gallery_viewed', {
        gallery_type: 'before_after',
        total_pairs: imagePairs.length,
        total_images: allImages.length
      });
      setGalleryViewed(true);
    }
  }, [galleryViewed]);

  const openAt = (i) => { 
    setIndex(i); 
    setIsOpen(true);
    
    // Track lightbox open
    const pairIndex = Math.floor(i / 2);
    const imageType = i % 2 === 0 ? 'before' : 'after';
    const pair = imagePairs[pairIndex];
    
    trackGalleryEvent('gallery_image_opened', {
      image_index: i,
      pair_id: pair.id,
      pair_title: pair.title,
      image_type: imageType,
      pair_index: pairIndex + 1
    });
  };

  const close = useCallback(() => {
    setIsOpen(false);
    
    // Track lightbox close
    const pairIndex = Math.floor(index / 2);
    const imageType = index % 2 === 0 ? 'before' : 'after';
    const pair = imagePairs[pairIndex];
    
    trackGalleryEvent('gallery_image_closed', {
      image_index: index,
      pair_id: pair.id,
      pair_title: pair.title,
      image_type: imageType,
      pair_index: pairIndex + 1
    });
  }, [index]);

  const prev = useCallback(() => {
    const newIndex = (index - 1 + allImages.length) % allImages.length;
    setIndex(newIndex);
    
    // Track navigation
    const pairIndex = Math.floor(newIndex / 2);
    const imageType = newIndex % 2 === 0 ? 'before' : 'after';
    const pair = imagePairs[pairIndex];
    
    trackGalleryEvent('gallery_navigation', {
      direction: 'previous',
      from_index: index,
      to_index: newIndex,
      pair_id: pair.id,
      pair_title: pair.title,
      image_type: imageType,
      pair_index: pairIndex + 1
    });
  }, [index]);

  const next = useCallback(() => {
    const newIndex = (index + 1) % allImages.length;
    setIndex(newIndex);
    
    // Track navigation
    const pairIndex = Math.floor(newIndex / 2);
    const imageType = newIndex % 2 === 0 ? 'before' : 'after';
    const pair = imagePairs[pairIndex];
    
    trackGalleryEvent('gallery_navigation', {
      direction: 'next',
      from_index: index,
      to_index: newIndex,
      pair_id: pair.id,
      pair_title: pair.title,
      image_type: imageType,
      pair_index: pairIndex + 1
    });
  }, [index]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        close();
        trackGalleryEvent('gallery_keyboard_interaction', {
          key: 'escape',
          action: 'close'
        });
      }
      if (e.key === 'ArrowLeft') {
        prev();
        trackGalleryEvent('gallery_keyboard_interaction', {
          key: 'arrow_left',
          action: 'previous'
        });
      }
      if (e.key === 'ArrowRight') {
        next();
        trackGalleryEvent('gallery_keyboard_interaction', {
          key: 'arrow_right',
          action: 'next'
        });
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, close, prev, next]);

  // Track image clicks in gallery view
  const handleImageClick = (pairIndex, imageType, pair) => {
    const imageIndex = pairIndex * 2 + (imageType === 'after' ? 1 : 0);
    openAt(imageIndex);
    
    trackGalleryEvent('gallery_thumbnail_clicked', {
      pair_id: pair.id,
      pair_title: pair.title,
      image_type: imageType,
      pair_index: pairIndex + 1,
      image_index: imageIndex
    });
  };

  return (
    <section className="section" id="gallery">
      <div className="container">
        {withHeading && <h2 className="section-title">Before & After Gallery</h2>}
        <div className="before-after-gallery">
          {imagePairs.map((pair, pairIndex) => (
            <div key={pair.id} className="before-after-pair">
              <h3 className="pair-title">{pair.title}</h3>
              <p className="pair-description">{pair.description}</p>
              <div className="before-after-images">
                <div className="image-container">
                  <div className="image-label before-label">Before</div>
                  <img
                    src={pair.before}
                    alt={`Before: ${pair.title}`}
                    loading="lazy"
                    onClick={() => handleImageClick(pairIndex, 'before', pair)}
                    style={{ cursor: 'zoom-in' }}
                  />
                </div>
                <div className="image-container">
                  <div className="image-label after-label">After</div>
                  <img
                    src={pair.after}
                    alt={`After: ${pair.title}`}
                    loading="lazy"
                    onClick={() => handleImageClick(pairIndex, 'after', pair)}
                    style={{ cursor: 'zoom-in' }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {isOpen && (
        <div className="lightbox" role="dialog" aria-modal="true" onClick={close}>
          <button 
            className="lightbox-close" 
            aria-label="Close" 
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
          >
            ×
          </button>
          <button 
            className="lightbox-prev" 
            aria-label="Previous" 
            onClick={(e) => { 
              e.stopPropagation(); 
              prev(); 
            }}
          >
            ‹
          </button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img className="lightbox-img" src={allImages[index]} alt={`Gallery ${index + 1}`} />
          </div>
          <button 
            className="lightbox-next" 
            aria-label="Next" 
            onClick={(e) => { 
              e.stopPropagation(); 
              next(); 
            }}
          >
            ›
          </button>
        </div>
      )}
    </section>
  );
};

export default Gallery;
